import type { UserAttribution } from '@/utils/attribution'

export const QuestionType = {
  RADIO_BUTTON: 'RADIO_BUTTON',
  CHECKBOX: 'CHECKBOX',
  NUMBER: 'NUMBER',
  TEXT: 'TEXT',
  SELECT: 'SELECT',
} as const

type BaseQuestion = {
  id: number
  title: string
  workflow_id: number | null
}

type Option = {
  id: number
  description: string
}

export type CheckboxQuestion = BaseQuestion & {
  type: typeof QuestionType.CHECKBOX
  options: Option[]
}

type NumberQuestion = BaseQuestion & {
  type: typeof QuestionType.NUMBER
}

type TextQuestion = BaseQuestion & {
  type: typeof QuestionType.TEXT
}

type SelectQuestion = BaseQuestion & {
  type: typeof QuestionType.SELECT
  options: Option[]
}

type RadioButtonQuestion = BaseQuestion & {
  type: typeof QuestionType.RADIO_BUTTON
  options: Option[]
}

export type Question =
  | SelectQuestion
  | TextQuestion
  | NumberQuestion
  | CheckboxQuestion
  | RadioButtonQuestion

export type Answer = {
  id: number
  value: string
  gate: string | null
  workflow_id: number | null
  question_option_id?: number
  game_id: number
}

export type Workflow = {
  title: string
  questions: Question[]
}

export type GameResult = {
  results: {
    [key: string]: {
      [key: string]: {
        score: number
        userId: string
        breakdown?: {
          [key: string]: {
            points: number
            maxPoints: boolean
          }
        }
      }
    } & { roundId: string }
  }
  positions: GameParticipantWithPosition[]
  holes_played: Record<string, string>
  penalties: Record<string, string>
}

export type PositionData = {
  score: number
  position: number
  first_place: number
  index: number
}

export interface Team {
  id: number
  name: string
  image: string
}

interface Player {
  id: number
  name: string
  image: string
}

export type SelectionRecord = {
  matches: {
    [match_id: string]: {
      selections: { unit_id: number; score: number; winner: boolean }[]
      eliminated: boolean
      points: null | number
      selected_unit_index: null | number
      position: string | null
    }
  }
  tiebreakers: Array<{
    round_id: number
    answer: number
  }>
  yankee_return: number | null
}

export type SelectionByMatchRecord = {
  selections: { unit_id: number; score: number; winner: boolean }[]
  eliminated: boolean
  selected_unit_index: number
  points: number
  user: {
    id: number
    nickname: string
  }
}

export type SelectionsByMatch = {
  items: Array<SelectionByMatchRecord>
  total: number
  offset: number
  limit: number
}

type UpdateSelectionRecord = {
  selections: {
    round_id: number
    match_id: number
    score: number
    unit_id: number
    winner: boolean
  }[]
  tiebreaker: number
}

interface SelectionOption {
  id: number
  name: string
  teams: Team[]
  players: Player[]
  start_date: Date
}

type MatchStatus = 'UPCOMING' | 'ACTIVE' | 'FINISHED' | 'POSTPONED' | 'CANCELLED'

export const RoundStatusLabels = {
  UPCOMING: 'Upcoming',
  ACTIVE: 'Active',
  FINISHED: 'Finished',
  POSTPONED: 'Postponed',
  CANCELLED: 'Cancelled',
} as const

export const RoundStatus = {
  UPCOMING: 'UPCOMING',
  ACTIVE: 'ACTIVE',
  FINISHED: 'FINISHED',
  POSTPONED: 'POSTPONED',
  CANCELLED: 'CANCELLED',
} as const

export const MatchStatusObj = RoundStatus

type UnitType = 'TEAM' | 'PLAYER' | 'HORSE'

type UnitPlace = 'HOME' | 'AWAY'

export type HorseDetail = {
  horse_number: number | null
  form: string | null
  age: number | null
  jockey: string | null
  trainer: string | null
  odds: string | null
}

export type Unit = {
  id: number
  created: string
  updated: string
  name: string
  short_code?: string | null
  image: string
  country: string
  sport_id: number
  unit_type: UnitType
  place: UnitPlace
  score: number
  horse_detail?: HorseDetail | null
}

export type Round = {
  id: number
  created: string
  updated: string
  name: string
  status: keyof typeof RoundStatusLabels
  start_date: string
  matches: Match[]
  tiebreaker_question: string
  tiebreaker_answer: string
}

export type RoundUserSelection = {
  round_id: number
  match_id: number
  unit_id: number
  score: number | null
  winner: boolean | null
}

export type RoundUserData = {
  selections: RoundUserSelection[]
  tiebreaker_answer: number | null
}

export type RoundWithUserData = Round & {
  user_data: RoundUserData | null
}

export type SimpleRound = {
  id: number
  name: string
  status: keyof typeof RoundStatus
  start_date: string
}

type Font = {
  url: string
  family: string
}

type FontsSchema = {
  main_font: Font
  secondary_font: Font
  auxiliary_font: Font
}

export type Match = {
  id: number
  created: string
  updated: string
  name: string
  start_date: string
  status: MatchStatus
  sport_id: number
  tournament_id: number
  match_information: string | null
  units: Unit[]
}

export interface LeaderboardParticipant {
  nickname: string
  id: number
}

export interface LeaderboardResponse {
  items: LeaderboardRecord[]
  total: number
  offset: number
  limit: number
}

export interface LeaderboardRecord {
  id: number
  position: string
  result: number
  payout: string
  user: LeaderboardParticipant
}

type GameParticipantWithPosition = {
  id: string
  nickname: string
  email: string
  position: number
}
type GameStatus = 'UPCOMING' | 'ACTIVE' | 'FINISHED' | 'READY_FOR_SETTLE' | 'CANCELLED'

export const GameType = {
  SR: 'SR',
  WDW: 'WDW',
  LMS: 'LMS',
  PTW: 'PTW',
  DRAFT: 'DRAFT',
  MARGIN: 'MARGIN',
  ODDS: 'ODDS',
} as const

type Sport = {
  id: number
  name: string
  image_url?: string
  opacity?: number
}

type Tournament = {
  id: number
  created: string
  updated: string
  name: string
  description: string
  image_url: string
  sport_id: number
  payout: string
}

type GameConfig = {
  id: number
  name: string
  rounds_ordering: number[]
  sport_id: number
  sport: Sport
  tournament_id: number
}

export type Participant = {
  id: number
  auth_id: string
  nickname: string
}

const PotShare = {
  WINNER_TAKES_ALL: 'WINNER_TAKES_ALL',
  GOLD_SILVER_BRONZE: 'GOLD_SILVER_BRONZE',
} as const

export type LeagueItem = {
  id: number
  image_url: string
  logo: string | null
  description: string
  name: string
  start_time: string
  pincode: string
  is_private: boolean
  creator_id: number
}

export interface GameInfo {
  id: number
  name: string
  start_time: string
  pincode: string
  description: any
  game_config_id: number
  parent_game_id: any
  active_round_index: number
  active_round_id: number
  participant_count: number
  rounds: SimpleRound[]
  game_config: GameConfig
  status: GameStatus
  logo: string
  selection_type: keyof typeof GameType
  creator_id: number
  rounds_joined?: { round_id: number; is_user_joined: boolean }[]
}

export const GolfPlayerStatus = {
  DQ: 'DQ',
  WD: 'WD',
  CUT: 'CUT',
  ACTIVE: 'ACTIVE',
} as const

export type GolfRoundData = {
  round_number: number
  total_to_par: number
  tee_time: string | null
  strokes: number | null
}

export type GolfData = {
  total_to_par: number
  holes_played: number
  status: keyof typeof GolfPlayerStatus | null
  current_round_number: number | null
  last_finished_round_score: number | null
  strokes: number | null
  rounds: Record<number, GolfRoundData>
}

export interface MiniLeagueResponse extends GameInfo {
  mini_league_position: number
  parent_game_id: number
}

export type SelectionUpdateObj = {
  round_id: number
  match_id: number
  score: number | null
  unit_id: number
  winner: boolean | null
}

export type PopUpData = {
  sport: number
  tournament: number
  selection_type: string
}

export type FeatureFlags = {
  prize_advertisement: boolean
  wufoo_forms: boolean
  game_header_white_bg: boolean
  sign_up_charity_page: boolean
  sign_up_dob: boolean
  eighteen_plus_entry: boolean
  sign_up_nationality: boolean
  sign_up_address: boolean
  sign_up_country_of_residence: boolean
  sign_up_extra_data_collection: boolean
  sign_up_team_name: boolean
  sign_up_collect_first_and_last_name: boolean
  sign_up_additional_questions: Question[]
  draft_additional_information: boolean
  no_hunch_opt_in: boolean
  public_prize_how_to_play?: boolean
  is_star_sports_page: boolean
  show_available_games?: boolean
  is_tbl_page?: boolean
  show_game_featured_card?: boolean
  show_prize_draw_featured_card?: boolean
  show_players_entered?: boolean
}

export type PopUpContent = {
  key: string
  sport: number
  tournament: number
  data: string
  title: string
  selection_type: string
  category: string
}

export type PartnerPageData = {
  id: string | number
  company_name: string
  company_domain: string
  header_line_1: string
  header_line_2: string
  company_url: string
  game_id: number
  highlighted_game_id?: number | null
  highlighted_raffle_id?: number | null
  logo: string
  landing_logo: string
  prize_draw_logo?: string
  game_featured_image?: string | null
  prize_draw_featured_image?: string | null
  leaderboard_advertisement: string
  metadata_content: string
  metadata_image: string
  site_title: string
  favicon: string
  background_color: string
  header_color: string
  secondary_header_color?: string
  footer_text_color: string
  primary_text_color: string
  secondary_text_color: string
  primary_button_color: string
  secondary_button_color: string
  primary_button_text_color: string
  secondary_button_text_color: string
  landing_bg_color: string
  landing_wave_color: string
  get_started_box_color: string
  landing_cta_text_color: string
  game_page_first_header_color: string
  game_page_second_header_color: string
  fonts: FontsSchema
  base_game_text_color: string
  landing_promotional_text_color: string
  links: Array<{ title: string; url: string }>
  join_game_toast_message?: string
  prize_header: string | null
  prize_description: string | null
  prize_image: string | undefined
  charity_page_header: string | null
  charity_page_description: string | null
  charity_page_image: string | null
  charity_page_breadcrumb_name: string | null
  charity_slug?: string
  feature_flags: FeatureFlags
  promotional_text_1: string | null
  promotional_text_2: string | null
  wufoo_form_hash: string | null
  landing_image: string | null
  landing_image_opacity: string | null
  cta_button_text: string | null
  hotjar_id: string | null
  hotjar_sv: string | null
  meta_pixel_id?: string | null
  organization_id: string
  is_external_login: boolean
  external_login_provider_id: number | null
  contact_us_email?: string
  promotional_text_1_color: string

  pwa_app_name?: string
  pwa_short_name?: string
  pwa_description?: string

  pwa_icon_180?: string
  pwa_icon_192?: string
  pwa_icon_512?: string
}

export interface RecentWinner {
  display_name: string
  prize_label: string
  amount: number | null
  amount_display: string | null
  win_count: number
  source: string
  won_at: string
}

export interface RecentWinnersResponse {
  partner_slug: string
  winners: RecentWinner[]
  active_players_count: number
  generated_at: string
}

export type MenuTabs = 'leaderboard' | 'predictions' | 'stats'

type MiniLeague = {
  id: number
  name: string
  participants: string[]
  mini_league_position: number
}

export type Addresses = {
  address: string
  url: string
  id: string
}

export type RegisterDTO = {
  email: string
  password: string
  nickname?: string
  first_name?: string
  last_name?: string
  dob?: string
  address?: string
  nationality?: string
  country_of_residence?: string
  team_name?: string
  organization_id: string
  partner_id?: string
  affiliate_ref?: string
  attribution?: UserAttribution
}

export type ExternalLoginRequest = {
  email: string
  organization_id: string
  external_login_provider_id?: number
  partner_id: string
}

export type ComplianceCheckResult = {
  is_compliant: boolean
  can_play: boolean
  reasons: string[]
}

export type ExternalLoginResponse = {
  requires_verification: boolean
  compliance_check: ComplianceCheckResult
  access_token?: string
  refresh_token?: string
  message?: string
}

export type GameJoinResponse = {
  id: number
  creator: string
  game_template_id: number
  sport_id: number
  tournament_id: number
  matches: number[]
  created: Date
  name: string
  description: string
  entry_stake: number
  selection_type: string
  game_options: {}
  participants: string[]
  is_b2b: boolean
}

export type StatisticsSelections = SelectionStatWithUnit[] | SelectionStatWithHomeAwayTeams[]

export type GameStatistics = {
  id: string
  sport_id: number
  sport_name: string
  tournament_id: number
  tournament_name: string
  game_id?: number
  game_name?: string
  total_selections: number
  statistics_type: string
  last_updated: number
  match: { id: number; name: string; start_date: string }
  selections: StatisticsSelections
}

export type SelectionStatWithUnit = {
  unit: StatUnit
  selections: number
  percentage: number
}

export type PlayerStatEvent = {
  metric_name: string
  raw_value: number
  points_awarded: number
  scored_at: string
}

export type UnitMatchStats = {
  unit_id: number
  unit_name: string
  short_code: string | null
  stats: PlayerStatEvent[]
}

export type SelectionStatWithHomeAwayTeams = {
  home: StatUnit
  away: StatUnit
  selections: number
  percentage: number
}

export type StatUnit = {
  name: string
  image: string
  score?: number
  id: number
}

export type UserProfile = {
  id: string
  email: string
  nickname: string
  first_name: string
  last_name: string
  dob: string
  picture: string
}

export type UserInfo = {
  nickname: string
  email: string
  fcm_token: string
  daily_limit: number
  weekly_limit: number
  monthly_limit: number
  id: number
  sub: string
  is_admin: boolean
  picture: string
  idv_status: string
  payment_blocked: boolean
  payment_blocked_reason: string
  blocked: boolean
  is_verified: boolean
  blocked_reason: string
}

export type SiteContent = {
  id: string
  partner: number
  content_type: SiteContentType
  content: string
  version?: number | null
  published_at?: string | null
}

export type SiteContentType =
  | 'GAME_RULES'
  | 'PRIVACY_POLICY'
  | 'TERMS_AND_CONDITIONS'
  | 'HOW_TO_PLAY'
  | 'PARTNER_TERMS_AND_CONDITIONS'

export type ScoringOption = 'ZERO' | 'BRONZE' | 'SILVER' | 'GOLD'

export type ScoringWord = {
  id: string
  sport: number
  scoring_option: ScoringOption
  content: string
}

export const enum LeaderboardDefaultScope {
  MULTI_ROUND = 'MULTI_ROUND',
  ROUND = 'ROUND',
}

export type JackpotInfo = {
  amount: string
  requirements: string
}

export type HowToPlayInfo = {
  title: string
  description: string
}

export type PrizeItem = {
  label: string
  value: string
}

export type PrizesInfo = {
  title: string
  prizes: PrizeItem[]
}

export type PrizeTierType = 'SITE_CREDIT' | 'CASH' | 'PHYSICAL_PRIZE' | 'FREE_ENTRY'

/** One row of a game's structured prize schedule: ranks start_rank..end_rank win this prize. */
export type PrizeTier = {
  id: number
  start_rank: number
  end_rank: number
  prize_type: PrizeTierType
  display_name: string
  amount: number | string | null
  description: string | null
  free_entry_count: number | null
  auto_credit: boolean
}

export type PointItem = {
  label: string
  value: string
}

export type PointsSystemInfo = {
  title: string
  points: PointItem[]
}

export enum GameImageFilter {
  BW = 'BW',
  ORIGINAL = 'ORIGINAL',
}

export type CharityPageConsentDecision = 'opted_in' | 'not_opted_in'
export type CharityPageConsentButtonClicked = 'primary' | 'secondary' | 'skip'

export type GameINFOV2 = {
  id: number
  active_round_index: number
  created: string
  updated: string
  name: string
  pincode: string
  start_time: string
  entry_stake: number
  leaderboard_default_scope: LeaderboardDefaultScope
  parent_game_id: number | null
  guaranteed_pot?: number
  pot_share: keyof typeof PotShare
  description: string
  participant_count: number
  status: GameStatus
  logo: string
  selection_type: keyof typeof GameType
  creator_id: number
  game_config_id: number
  max_round_count: number | null
  is_hospitality: boolean
  is_potm: boolean
  rounds: SimpleRound[]
  game_config: GameConfig
  organization_id: string
  prize_page_header?: string | null
  prize_page_description?: string | null
  prize_page_image?: string | null
  charity_page_enabled?: boolean
  charity_page_heading?: string | null
  charity_page_description?: string | null
  charity_page_image?: string | null
  charity_page_consent_language?: string | null
  charity_page_prize_info_url?: string | null
  charity_page_prize_info_label?: string | null
  charity_page_privacy_url_1?: string | null
  charity_page_privacy_url_2?: string | null
  charity_page_terms_url?: string | null
  charity_page_cta_primary_label?: string | null
  charity_page_cta_primary_url?: string | null
  charity_page_cta_primary_type?: 'LINK' | 'OPT_IN'
  charity_page_cta_secondary_label?: string | null
  charity_page_cta_secondary_enabled?: boolean
  charity_page_show_share_link?: boolean
  game_rules?: string | null
  header_line_1: string | null
  header_line_2: string | null
  jackpot?: JackpotInfo | null
  how_to_play?: HowToPlayInfo | null
  prizes?: PrizesInfo | null
  prize_tiers?: PrizeTier[]
  points_system?: PointsSystemInfo | null
  is_in_progress?: boolean
  is_battle_royale?: boolean
  rounds_joined?: { round_id: number; is_user_joined: boolean }[]
  image_url?: string | null
  image_filter?: GameImageFilter
}

export type UnitWithPoints = {
  unit: Unit | undefined
  points: number | null
}

export type AdditionalQuestion = {
  title: string
  field_name: string
  field_type: string
  place: string
}

export type AdditionalAnswer = {
  place: string
  answer: string | boolean | number
  question: string
  field_name: string
  partner_domain: string
  organization_id: string
}
